/**
 * Fila de recompensas animada para la escena Resultados: monedas, XP y una
 * pieza por cada gear obtenido, entrando con un stagger de escala.
 */

import Phaser from 'phaser';
import { TEX } from '../core/TextureFactory';
import type { GearPiece } from '../core/types';
import { gear as gearDefs } from '../data';
import type { RunRewardResult } from '../meta';
import { PALETTE, rarityColor, slotColor, slotLabel } from '../ui/lobby/palette';
import { formatCompact } from '../ui/lobby/uiKit';

const MAX_GEAR_SHOWN = 6;

function gearTileTexture(piece: GearPiece): { rarity: string } {
  const def = gearDefs.find((d) => d.setKey === piece.setKey && d.slot === piece.slot);
  const rarity = def?.rarityByTier[piece.tier - 1] ?? 'comun';
  return { rarity };
}

export class ResultsRewardRow extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x: number, y: number, width: number, reward: RunRewardResult) {
    super(scene, x, y);

    const items: Phaser.GameObjects.Container[] = [];

    if (reward.coins > 0) {
      items.push(this.buildCoinItem(scene, reward.coins));
    }
    if (reward.xp > 0) {
      items.push(this.buildXpItem(scene, reward.xp));
    }

    const shown = reward.loot.slice(0, MAX_GEAR_SHOWN);
    for (const piece of shown) {
      items.push(this.buildGearItem(scene, piece));
    }
    const hiddenCount = reward.loot.length - shown.length;
    if (hiddenCount > 0) {
      items.push(this.buildMoreItem(scene, hiddenCount));
    }

    if (items.length === 0) {
      const emptyText = scene.add
        .text(0, 0, 'Sin recompensas esta vez.', {
          fontFamily: 'monospace',
          fontSize: '13px',
          color: '#6f6394',
        })
        .setOrigin(0.5);
      this.add(emptyText);
      scene.add.existing(this);
      return;
    }

    const itemSize = 76;
    const gap = 10;
    const columns = Math.min(items.length, Math.max(1, Math.floor((width + gap) / (itemSize + gap))));
    const rows = Math.ceil(items.length / columns);
    const gridWidth = columns * itemSize + (columns - 1) * gap;

    items.forEach((item, i) => {
      const col = i % columns;
      const row = Math.floor(i / columns);
      item.setPosition(
        -gridWidth / 2 + col * (itemSize + gap) + itemSize / 2,
        row * (itemSize + gap) - ((rows - 1) * (itemSize + gap)) / 2
      );
      item.setScale(0);
      this.add(item);
    });

    scene.add.existing(this);
    this.playEntrance(scene, items);
  }

  private playEntrance(scene: Phaser.Scene, items: Phaser.GameObjects.Container[]): void {
    items.forEach((item, i) => {
      scene.tweens.add({
        targets: item,
        scale: 1,
        duration: 260,
        delay: 150 + i * 90,
        ease: 'Back.Out',
      });
    });
  }

  private buildCoinItem(scene: Phaser.Scene, amount: number): Phaser.GameObjects.Container {
    const c = scene.add.container(0, 0);
    const bg = scene.add.graphics();
    bg.fillStyle(PALETTE.panel2, 1);
    bg.fillRoundedRect(-34, -34, 68, 68, 12);
    bg.lineStyle(2, PALETTE.accent, 1);
    bg.strokeRoundedRect(-33, -33, 66, 66, 12);
    const icon = scene.add.image(0, -10, TEX.iconCoin).setDisplaySize(28, 28);
    const text = scene.add
      .text(0, 20, `+${formatCompact(amount)}`, {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#ffd166',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    c.add([bg, icon, text]);
    return c;
  }

  private buildXpItem(scene: Phaser.Scene, amount: number): Phaser.GameObjects.Container {
    const c = scene.add.container(0, 0);
    const bg = scene.add.graphics();
    bg.fillStyle(PALETTE.panel2, 1);
    bg.fillRoundedRect(-34, -34, 68, 68, 12);
    bg.lineStyle(2, 0x6fcfff, 1);
    bg.strokeRoundedRect(-33, -33, 66, 66, 12);
    const star = scene.add.graphics();
    star.fillStyle(0x6fcfff, 1);
    drawStar(star, 0, -10, 12);
    const text = scene.add
      .text(0, 20, `+${formatCompact(amount)} XP`, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#6fcfff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    c.add([bg, star, text]);
    return c;
  }

  private buildGearItem(scene: Phaser.Scene, piece: GearPiece): Phaser.GameObjects.Container {
    const { rarity } = gearTileTexture(piece);
    const c = scene.add.container(0, 0);
    const bg = scene.add.graphics();
    bg.fillStyle(slotColor(piece.slot), 0.28);
    bg.fillRoundedRect(-34, -34, 68, 68, 12);
    bg.lineStyle(3, rarityColor(rarity), 1);
    bg.strokeRoundedRect(-33, -33, 66, 66, 12);
    const label = scene.add
      .text(0, -8, slotLabel(piece.slot), {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#f4f0ff',
      })
      .setOrigin(0.5);
    const tier = scene.add
      .text(0, 12, `T${piece.tier}`, {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: '#ffd166',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    c.add([bg, label, tier]);
    return c;
  }

  private buildMoreItem(scene: Phaser.Scene, count: number): Phaser.GameObjects.Container {
    const c = scene.add.container(0, 0);
    const bg = scene.add.graphics();
    bg.fillStyle(PALETTE.panel2, 1);
    bg.fillRoundedRect(-34, -34, 68, 68, 12);
    bg.lineStyle(2, PALETTE.borderLight, 1);
    bg.strokeRoundedRect(-33, -33, 66, 66, 12);
    const text = scene.add
      .text(0, 0, `+${count}`, {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: '#a99fd1',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    c.add([bg, text]);
    return c;
  }
}

function drawStar(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number): void {
  const points: Phaser.Math.Vector2[] = [];
  for (let i = 0; i < 10; i++) {
    const radius = i % 2 === 0 ? r : r * 0.45;
    const angle = (Math.PI / 5) * i - Math.PI / 2;
    points.push(new Phaser.Math.Vector2(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius));
  }
  g.fillPoints(points, true);
}
